import React, { useCallback, useEffect, useMemo, useRef, useState, useContext } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import styled from 'styled-components'
import { rem } from 'polished'
import { replace, RouterLocation } from 'connected-react-router'
import cx from 'clsx'

import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import OfferGallery from '../OfferGallery'
import Pane from 'components/Common/Pane'

import Backdrop from 'components/utils/Backdrop'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import OfferIntro from './BundleAndSaveIntro'
import noop from 'lib/noop'
import config from 'constants/config'
import Breakpoint from 'components/Common/Breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import {
  fetchAvailableRatesForOffer,
} from 'actions/OfferActions'
import BundleAndSaveSidebarFilters from './BundleAndSaveSidebarFilters'
import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'
import { OfferPageActions, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import useMountedEffect from 'hooks/useMountedEffect'
import { clearBookingCalendar, fetchCalendars } from 'actions/CalendarActions'
import usePrevious from 'hooks/usePrevious'
import { removeSearchParamsFromURL } from 'lib/searchUtils'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import { OFFER_TYPE_HOTEL_ANALYTICS } from 'constants/offer'
import { GA_ACTION_TO_SNOWPLOW_MAP, CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import { urlHashDatePicker } from 'constants/url'
import { fireInteractionEvent, fireNonInteractionEvent } from 'api/googleTagManager'
import { calendarAction, offerPageWatchVideo } from 'analytics/eventDefinitions'
import { getInitialBundleAndSaveOfferPageState } from 'selectors/offerPageSelectors'
import Heading from 'components/Luxkit/Typography/Heading'
import OfferUpgradeModalContext from '../OfferUpgradeModalContext'
import { generateRoomOccupants } from 'lib/occupancyUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import offerPageURL from 'lib/offerPageURL'
import LightBoxContext from 'components/Luxkit/Modal/LightBoxContext'
import VideoLightbox from 'components/Luxkit/Carousel/VideoLightbox'
import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'
import * as Analytics from 'analytics/analytics'
import { offerEvent } from 'analytics/snowplow/events'

import { getGroupedBundledPackages } from 'lib/bundleAndSave/getGroupedBundledPackages'
import BundleAndSaveOptionsSection from './BundleAndSaveOptionsSection'
import Group from 'components/utils/Group'
import { sortBy, max } from 'lib/arrayUtils'
import BodyText from 'components/Luxkit/Typography/BodyText'
import OfferCapacityWithAgesSelect from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import BundleAndSaveDateSelection from './BundleAndSaveDateSelection/BundleAndSaveDateSelection'
import BundleAndSaveSwapModal from 'components/OfferPage/BundleAndSave/BundleAndSaveSwapModal'
import { useIsMobileScreen } from 'lib/deviceUtils'
import BundleAndSaveHeader from './BundleAndSaveHeader'

const RelativeBlock = styled.form`
  position: relative;
`

/* This is unindeal, due to the imparted being wrapped in a HoC, styling it directly with a SC won't work.  */
// TODO - Fix up 'OfferCapacityWithAgesSelectBox
const OfferCapacityWithAgesSelectBoxStyleImparter = styled.div`
  & button {
    padding: ${rem(12)};
    border-radius: ${({ theme }) => theme.borderRadius.medium};
  }
`

/* Ensures the selection modals render fixed to the bottom of their triggers */
const OCCUPANCY_Z_INDEX = 3
const DATE_SELECT_Z_INDEX = 5

const SelectionGroup = styled(Group)`
  position: relative;

  &.occupancy {
    z-index: ${OCCUPANCY_Z_INDEX};
    pointer-events: none;
  }

  &.dates {
    z-index: ${DATE_SELECT_Z_INDEX};
    pointer-events: none;
  }
`

const BundleOptionSelectionSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(32)};
  width: 100%;
  background-color: ${(props) => props.theme.palette.neutral.six};
  padding: ${rem(40)} 0;
`

type CartDefaults = Partial<OfferPageState>

interface Props {
  defaults: CartDefaults;
  offer: App.BundleOffer;
  onFilterClose?: () => void;
  routerLocation: RouterLocation<unknown>;
  currency: string;
  isMultiRoomEnabled: boolean;
  regionCode: string;
}

const fallbackRooms = [generateRoomOccupants()]

// const lereTimingAnalyticsCategory = 'BundleAndSaveContent - LERE'
// const lereTimingType = 'lereBundleAndSaveRenderReady'

function BundleAndSaveContent(props: Props) {
  const {
    offer,
    defaults,
    onFilterClose,
    routerLocation,
    isMultiRoomEnabled,
    regionCode,
  } = props

  const {
    id,
    packages,
  } = offer

  // calendars only start fetching once the user has tried to look at dates
  // after that, try keep the calendars up to date
  const [viewedDates, setViewedDates] = useState(Object.values(defaults?.bundledOffers ?? {}).some(p => !!p.checkInDate))
  const [state, stateDispatch, stateSnapshotRef] = useOfferPageReducer(defaults)

  const {
    airport,
    activeFilter,
    rooms = fallbackRooms,
    bundledOffers,
    bundleOffer,
  } = state

  const [firstHotelOffer, secondHotelOffer] = useMemo(
    () => Object.values(bundledOffers ?? {}),
    [bundledOffers])

  const isMobileScreen = useIsMobileScreen()
  const dispatch = useAppDispatch()
  const packageOptionsAnchor = useRef<HTMLDivElement>(null)
  const bundledOffersAnchor = useRef<HTMLDivElement>(null)
  const scrollAfterFilterClose = useRef<boolean>(false)
  const previousState = usePrevious<OfferPageState>(state, {})
  const previousFilter = usePrevious(activeFilter)
  const lastFetchedRooms = useRef<Array<App.RoomOccupants>>([])
  const lastFetchedAirport = useRef<App.Airport>()

  const isCalendarAlreadyOpen = Object.values(previousState.bundledOffers ?? {}).some((o) => o.checkInDate) || false

  const onUserAnalyticsAction = useCallback((action: string, label?: string) => {
    const event = calendarAction(OFFER_TYPE_HOTEL_ANALYTICS, action, label)
    fireInteractionEvent(event)

    const snowplowEvent = GA_ACTION_TO_SNOWPLOW_MAP[event.action]
    if (snowplowEvent) {
      const event = offerEvent(snowplowEvent, stateSnapshotRef.current)
      if (event) Analytics.track('offer-event', event)
    }
  }, [stateSnapshotRef])

  useIdleTimeReporter(IdleTimeSource.BundleAndSave)

  useMountedEffect(() => {
    const previousCheckInDates = previousState?.bundledOffers ?? {}
    const datesChanges = !Object.values(state.bundledOffers ?? {}).every((o) => o.offer && previousCheckInDates[o.offer.id].checkInDate === o.checkInDate)

    scrollAfterFilterClose.current = scrollAfterFilterClose.current ||
    previousState.rooms !== rooms ||
    datesChanges
  }, [state.rooms, state.bundledOffers])

  const scrollToPackageOptions = useCallback(() => {
    packageOptionsAnchor.current?.scrollIntoView()
  }, [])

  const scrollToBundledOffers = useCallback(() => {
    bundledOffersAnchor.current?.scrollIntoView()
  }, [])

  const onViewPackageOptions = useCallback(() => {
    scrollToPackageOptions()
    const event = offerEvent('VIEW_PACKAGE_OPTIONS_PRESSED', stateSnapshotRef.current)
    if (event) Analytics.track('offer-event', event)
  }, [scrollToPackageOptions, stateSnapshotRef])

  const fetchCalendarData = useCallback(() => {
    // if we have any invalid ages, don't try fetch - it'll fail anyway
    if (!rooms.some(room => room.childrenAge?.some(age => age === -1))) {
      const packagesData = Object.values(getGroupedBundledPackages(offer, Object.values(bundledOffers ?? {}).map((bOffer) => ({
        offerId: bOffer.offer.id,
        duration: bOffer.duration,
      })))).flat()

      if (packagesData.length) {
        dispatch(fetchCalendars(id, {
          occupants: rooms,
          packages: packagesData,
        }))
      }
    }

    lastFetchedRooms.current = rooms
    lastFetchedAirport.current = airport
  }, [airport, offer, id, bundledOffers, dispatch, rooms])

  useEffect(() => {
    for (const bundledOffer of Object.values(bundledOffers ?? {})) {
      const hasDate = !!bundledOffer.checkInDate
      const useDynamicCancellationPolicies = bundledOffer.offer?.property?.useDynamicCancellationPolicies ?? false

      if (hasDate && !activeFilter && useDynamicCancellationPolicies && bundledOffer.offer) {
        dispatch(fetchAvailableRatesForOffer(
          bundledOffer.offer,
          {
            occupants: rooms,
            checkIn: bundledOffer.checkInDate.format(ISO_DATE_FORMAT),
            checkOut: bundledOffer.checkInDate.clone().add(bundledOffer.duration, 'days').format(ISO_DATE_FORMAT),
            bundleOfferId: bundleOffer.id,
          },
        ))
      }
    }
  }, [dispatch, bundledOffers, bundleOffer, activeFilter, rooms])

  const showLightbox = useContext(LightBoxContext)

  const watchVideo = useCallback(() => {
    fireInteractionEvent(offerPageWatchVideo())
    showLightbox(<VideoLightbox videoId={offer.vimeoVideoId} playOnMount />)
  }, [offer, showLightbox])

  useEffect(() => {
    const hasRooms = rooms !== fallbackRooms
    const hasDates = Object.values(state?.bundledOffers ?? {}).every((o) => o.checkInDate)
    const hasInvalidChildren = rooms.some(room => room.childrenAge?.some(age => age === -1))

    if (previousFilter === 'capacity') {
      if (!hasRooms) {
        // user looked at capacity, didn't change anything and closed it
        // we consider this them applying the default rooms, so dispatch a proper update
        stateDispatch({
          type: OfferPageActions.setRooms,
          rooms: [generateRoomOccupants()],
        })

        if (!activeFilter) {
          scrollToPackageOptions()
          scrollAfterFilterClose.current = false
        }
      }
      if (!activeFilter && !hasDates && !hasInvalidChildren) {
        stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
        fireInteractionEvent(calendarAction(offer.analyticsType, CALENDAR_GA_EVENT_ACTION.OpenFilter, CALENDAR_GA_EVENT_LABEL.PackagePaneHeader))
      }
    }

    if (activeFilter === 'checkin' && hasInvalidChildren) {
      // trying to open the calendar with invalid children ages, means we can't fetch calendar
      // direct them to the capacity instead to fill it in
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
    }

    if (previousFilter === 'checkin' && !activeFilter && hasDates && !hasRooms) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
    }

    if (activeFilter) {
      if (activeFilter === 'checkin') {
        scrollToBundledOffers()
      } else {
        scrollToPackageOptions()
      }
    }

    if (previousFilter && !activeFilter && hasRooms && hasDates) {
      if (scrollAfterFilterClose.current) {
        if (isMobileScreen && firstHotelOffer?.checkInDate && secondHotelOffer?.checkInDate) {
          const firstPricingBlock = document.getElementsByClassName('pricing')[0]?.getBoundingClientRect()
          const winScroll =
            document.body.scrollTop || document.documentElement.scrollTop
          const winHeight = window.innerHeight
          if (winScroll && firstPricingBlock) {
            const adjust = winHeight ? winHeight - firstPricingBlock.height - 40 : 40
            window.scrollTo({
              behavior: 'auto',
              top: winScroll + firstPricingBlock.top - adjust,
            })
          }
        } else {
          scrollToPackageOptions()
        }
      }
      scrollAfterFilterClose.current = false
    }

    if (previousFilter && !activeFilter && hasDates && state?.bundledOffers) {
      const firstBundledOfferId = Object.keys(state.bundledOffers)[0]
      const sortedBundledOffers = sortBy(Object.values(state.bundledOffers), (p) => {
        return p.checkInDate ? p.checkInDate.toDate().getTime() : 0
      }, 'asc')

      if (firstBundledOfferId !== sortedBundledOffers[0].offer?.id) {
        stateDispatch({ type: OfferPageActions.reverseBundles })
      }
    }

    if (activeFilter === 'checkin' && !viewedDates) {
      setViewedDates(true)
      fetchCalendarData()
    } else {
      if (viewedDates && lastFetchedRooms.current !== rooms) {
        dispatch(clearBookingCalendar())
        fetchCalendarData()
      }
    }
  }, [activeFilter, firstHotelOffer, secondHotelOffer]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // once we load packages and have dates, get our calendar data
    if (viewedDates && offer.packages) {
      fetchCalendarData()
    }
  // eslint-disable-next-line
  }, [offer.packages])

  useEffect(() => {
    if (defaults.offer && defaults.bundledOffers && previousState?.bundledOffers) {
      if (previousState?.offer?.packages?.length !== defaults.offer.packages.length) {
        const bundledOffersUpdates = Object.keys(defaults.bundledOffers).reduce((acc, itemKey) => {
          const { checkInDate, checkOutDate, duration } = previousState?.bundledOffers?.[itemKey] ?? {}

          if (checkInDate && checkOutDate) {
            acc[itemKey].checkOutDate = checkOutDate
            acc[itemKey].checkInDate = checkInDate
            acc[itemKey].duration = duration
          }

          return acc
        }, defaults.bundledOffers)

        stateDispatch({ type: OfferPageActions.setOffer, offer: defaults.offer, bundledOffers: bundledOffersUpdates })
      }
    }
  }, [stateDispatch, defaults, previousState])

  useMountedEffect(() => {
    // effectively listening on 'on duration change' event
    if (viewedDates) {
      fetchCalendarData()
    }

    if (!activeFilter) {
      scrollToPackageOptions()
    }
  }, [bundledOffers])

  const closeFilters = useCallback(() => {
    if (activeFilter === 'checkin') {
      onUserAnalyticsAction(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseOutside)
    }
    stateDispatch({ type: OfferPageActions.closeFilter })
    onFilterClose?.()
  }, [onFilterClose, onUserAnalyticsAction, activeFilter, stateDispatch])

  useEffect(() => {
    if (routerLocation.search) {
      const newSearch = removeSearchParamsFromURL(routerLocation.search)
      dispatch(replace({ ...routerLocation, search: newSearch }))
    }

    if (routerLocation.hash === urlHashDatePicker) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
      fireNonInteractionEvent(calendarAction(offer.analyticsType, CALENDAR_GA_EVENT_ACTION.OpenFilter, CALENDAR_GA_EVENT_LABEL.RouterLocation))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const occupancyLimits = useMemo(() => {
    if (config.ENABLE_OCCUPANCY_LIMITS) {
      const groupedByOffers = Object.values(bundledOffers ?? {}).map((o) => packages.filter(p => p.offerId === o.offer.id).flatMap((p) => p.roomRate.capacities))

      const maxAdults = groupedByOffers.map(x => max(x, o => o.adults)?.adults)
      const maxChildren = Math.min(...groupedByOffers.map(x => max(x, o => o.children)?.children ?? 0))
      const maxInfants = Math.min(...groupedByOffers.map(x => max(x, o => o.infants)?.infants ?? 0))

      const limits = maxAdults.reduce((acc, item, index) => {
        const [val] = acc

        if ((val ?? 0) > (item ?? 0) || !val) {
          return [item, index]
        }

        return acc
      }, [0, 0])

      const capacities = groupedByOffers[limits[1]] as App.PackageRoomCapacity[]

      return capacities.filter(o => o.adults <= limits[0] && o.children <= maxChildren && o.infants <= maxInfants)
    }

    return undefined
  }, [packages, bundledOffers])

  const availableForPurchase = useIsAvailableForPurchase(state)

  if (offer.altLink) {
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}${offerPageURL(offer)}`} />
  }

  return (
    <UserAnalyticsActionContext.Provider value={onUserAnalyticsAction}>
      <OfferPageStateProvider state={state} stateSnapshotRef={stateSnapshotRef} dispatch={stateDispatch}>
        <OfferUpgradeModalContext>
          <RelativeBlock onSubmit={(e) => e.preventDefault()}>
            {availableForPurchase &&
              <Breakpoint max="xs">
                <BundleAndSaveSidebarFilters
                  enableMultiRoomTypesEvents={isMultiRoomEnabled}
                  onViewPackageOptions={scrollToPackageOptions}
                  capacities={occupancyLimits}
                />
              </Breakpoint>
            }
            <BundleAndSaveHeader
              onViewPackageOptions={onViewPackageOptions}
              />
            <OfferGallery offer={offer} />
            <Pane>
              <OfferIntro
                onWatchVideo={watchVideo}
                onViewPackageOptions={onViewPackageOptions}
              />
            </Pane>
            <ScrollAnchor ref={packageOptionsAnchor} />
            <BundleOptionSelectionSection id="#Package-Options">
              <LayoutContainer>
                <Group direction="vertical" horizontalAlign="center" gap={24}>
                  <Heading variant="heading3" format="titlecase" as="h2" align="center">
                    Bundle Options
                  </Heading>
                  {availableForPurchase && (<>
                    <SelectionGroup className={cx({ occupancy: activeFilter === 'capacity' })} fullWidth direction="horizontal" horizontalAlign="center">
                      <OfferCapacityWithAgesSelectBoxStyleImparter>
                        <OfferCapacityWithAgesSelect offsetDropdown capacities={occupancyLimits} />
                      </OfferCapacityWithAgesSelectBoxStyleImparter>
                    </SelectionGroup>
                    <Group fullWidth direction="vertical" horizontalAlign="center" gap={12}>
                      <BodyText variant="large">When do you want to go?</BodyText>
                      <ScrollAnchor ref={bundledOffersAnchor} />
                      <SelectionGroup className={cx({ dates: activeFilter === 'checkin' })} fullWidth direction="horizontal" horizontalAlign="center">
                        <BundleAndSaveDateSelection
                          onClickLabel="Select dates"
                          offsetDropdown
                          isCalendarAlreadyOpen={isCalendarAlreadyOpen}
                        />
                      </SelectionGroup>
                      <Backdrop style={{ zIndex: OCCUPANCY_Z_INDEX - 1 }} onClick={closeFilters} visible={activeFilter === 'capacity'} />
                      <Backdrop style={{ zIndex: DATE_SELECT_Z_INDEX - 1 }} onClick={closeFilters} visible={activeFilter === 'checkin'} />
                    </Group>
                  </>)}
                </Group>
              </LayoutContainer>
              <BundleAndSaveOptionsSection availableForPurchase={availableForPurchase} />
            </BundleOptionSelectionSection>
            <BundleAndSaveSwapModal />
          </RelativeBlock>
        </OfferUpgradeModalContext>
      </OfferPageStateProvider>
    </UserAnalyticsActionContext.Provider>
  )
}

BundleAndSaveContent.defaultProps = {
  onFilterClose: noop,
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  return {
    regionCode: state.geo.currentRegionCode,
    defaults: getInitialBundleAndSaveOfferPageState(state, ownProps.offer),
    routerLocation: state.router.location,
    currency: state.geo.currentCurrency,
    isMultiRoomEnabled: false,
  }
}

export default connect(mapStateToProps)(BundleAndSaveContent)
